import { useState } from "react";
import { Link } from "react-router-dom";
import { Users, CalendarCheck, CreditCard, Search, LogOut, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Recipients } from "@/components/dashboard/Recipients";
import { Bookings } from "@/components/dashboard/Bookings";
import { Payments } from "@/components/dashboard/Payments";
import { Browse } from "@/components/dashboard/Browse";
import { Updates } from "@/components/dashboard/Updates";
import { AppDownloads } from "@/components/dashboard/AppDownloads";

const TABS = [
  { id: "recipients", label: "Care recipients", icon: Users },
  { id: "browse", label: "Find a caregiver", icon: Search },
  { id: "bookings", label: "Bookings & escrow", icon: CalendarCheck },
  { id: "updates", label: "Updates", icon: Bell },
  { id: "payments", label: "Quick book", icon: CreditCard },
] as const;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState<(typeof TABS)[number]["id"]>("recipients");
  if (!user) return null;

  return (
    <div className="min-h-screen bg-brand-ivory" data-testid="diaspora-dashboard">
      <header className="border-b border-brand-line bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-8">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-brand-slate sm:block" data-testid="dashboard-user-name">{user.name}</span>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-brand-line px-4 py-2 text-sm font-medium text-brand-navy transition hover:bg-slate-50" data-testid="logout-button"><LogOut className="h-4 w-4" />Log out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-emerald">Family portal</p>
        <h1 className="mt-2 font-heading text-3xl font-bold text-brand-navy sm:text-4xl">Welcome, {user.name.split(" ")[0]}.</h1>
        <p className="mt-2 text-brand-slate">Add the people you care for, then book a verified visit. Funds stay in escrow until check-out.</p>
        <AppDownloads />

        <div className="mt-10 flex gap-2 overflow-x-auto border-b border-brand-line" role="tablist">
          {TABS.map((t) => (
            <button key={t.id} role="tab" onClick={() => setTab(t.id)} className={`-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition ${tab === t.id ? "border-brand-blue text-brand-blue" : "border-transparent text-brand-slate hover:text-brand-navy"}`} data-testid={`dashboard-tab-${t.id}`}>
              <t.icon className="h-4 w-4" />{t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "recipients" && <Recipients />}
          {tab === "browse" && <Browse />}
          {tab === "bookings" && <Bookings />}
          {tab === "updates" && <Updates />}
          {tab === "payments" && <Payments />}
        </div>
      </main>
    </div>
  );
}
